import { useMutation, useQueryClient } from '@tanstack/react-query';

import { $$toast } from '@doger/ui/components/ui/toast';
import type { SessionResponse } from '@doger/core/shared/api';
import { $$api } from '@doger/core/shared/api';

import { useAnalytics } from '@doger/chat/shared/analytics';

import { useTranslation } from '../../../../shared/i18n';

export type RenameChatMutationParams = {
  chatId: string;
};

export type RenameChatMutationArgs = {
  chatId: string;
  newTitle: string;
};

export const useRenameChatMutation = ({ chatId }: RenameChatMutationParams) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('app');
  const analytics = useAnalytics();

  return useMutation({
    mutationKey: ['auth', 'sessions', 'rename', chatId],
    mutationFn: ({ chatId, newTitle }: RenameChatMutationArgs) =>
      $$api.editSessionTitle({
        sessionId: chatId,
        title: newTitle,
      }),

    onMutate: ({ chatId, newTitle }) => {
      queryClient.setQueryData(
        ['auth', 'sessions'],
        (oldData: SessionResponse[]) => {
          if (!oldData) return oldData;

          return oldData.map((s) =>
            s.id === chatId
              ? {
                  ...s,
                  meta: { ...s.meta, title: newTitle },
                }
              : s
          );
        }
      );
      queryClient.setQueryData(
        ['auth', 'session', chatId],
        (oldData: SessionResponse) => {
          if (!oldData) return oldData;

          return { ...oldData, meta: { ...oldData.meta, title: newTitle } };
        }
      );
    },

    onSuccess: (_, { chatId }) => {
      analytics.amplitude.track({
        event: 'User Renamed Chat',
        properties: { chatId },
      });
    },

    onError: (error) => {
      console.error('Failed to rename chat session:', error);
      $$toast.error(t('errors.chat.failedToRename'));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });
};
